import Container from "@/components/containers/Container";
import { Button } from "../../../components/ui/button";
import {
  ArrowRight,
  Coins,
  Users,
  ShieldCheck,
  Zap,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { cn } from "@/components/ui/utils";
import { DASHBOARD_PATH, LOCALSTORAGES } from "@/constants/constant";
import { useNavigate } from "react-router-dom";

const Steps = [
  {
    title: "Connect Wallet",
    subTitle: "Link your BSC wallet securely — takes just one click.",
    icon: Zap,
  },
  {
    title: "Complete Tasks",
    subTitle: "Do simple social actions like follow, like, or retweet.",
    icon: Sparkles,
  },
  {
    title: "Claim BINGOLD",
    subTitle: "Tasks are verified automatically — rewards sent instantly.",
    icon: Coins,
  },
];

export default function HeroSection() {
  const { openConnectModal } = useConnectModal();
  const [stats, setStats] = useState({
    successRate: 0,
    totalDistributed: 0,
    totalParticipants: 0,
  });
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { isConnected } = useAccount();
  const token = localStorage.getItem(LOCALSTORAGES.TOKEN);
  const navigate = useNavigate();

  // Live stats
  useEffect(() => {
    const ws = new WebSocket("wss://node-airdrop.bingold.to");
    ws.onopen = () => ws.send(JSON.stringify({ options: "getLiveStats" }));
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.data) {
          setStats({
            successRate: data.data.successRate || 0,
            totalDistributed: data.data.totalDistributed || 0,
            totalParticipants: data.data.totalParticipants || 0,
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    return () => ws.close();
  }, []);

  // Scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    const section = document.querySelector("#home");
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Auto-cycle steps
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % Steps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center bg-[#050505] text-white pt-32 pb-24 px-6 overflow-hidden"
    >
      {/* Full Hero Background Effect – No impact on cards/content */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large soft radial glows */}
        <div
          className={cn(
            "absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-600/8 via-transparent to-amber-600/8 blur-3xl transition-opacity duration-3000",
            isVisible ? "opacity-100" : "opacity-0"
          )}
        />
        <div
          className={cn(
            "absolute top-0 right-0 w-3/4 h-3/4 bg-gradient-to-bl from-amber-500/10 to-transparent rounded-full blur-3xl transition-opacity duration-3000 delay-500",
            isVisible ? "opacity-100" : "opacity-0"
          )}
        />
        <div
          className={cn(
            "absolute bottom-0 left-1/4 w-1/2 h-1/2 bg-gradient-to-tr from-yellow-500/8 to-transparent rounded-full blur-3xl transition-opacity duration-3000 delay-1000",
            isVisible ? "opacity-100" : "opacity-0"
          )}
        />

        {/* Floating golden particles */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-yellow-400/50 rounded-full animate-float-slow"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${15 + i * 3}s`,
              }}
            />
          ))}
        </div>

        {/* Subtle grain overlay for premium texture */}
        <div
          className={cn(
            "absolute inset-0 opacity-30 mix-blend-soft-light transition-opacity duration-2000",
            isVisible ? "opacity-30" : "opacity-0"
          )}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <Container>
        <div className="max-w-5xl mx-auto text-center space-y-20">
          {/* Live Badge */}
          <div
            className={cn(
              "inline-flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/20 px-6 py-3 rounded-full text-yellow-400 text-sm font-semibold transition-all duration-800",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            )}
          >
            <div className="relative">
              <span className="absolute inset-0 w-3 h-3 bg-yellow-500 rounded-full animate-ping opacity-75" />
              <span className="relative w-3 h-3 bg-yellow-500 rounded-full" />
            </div>
            Live on BSC Mainnet
          </div>

          {/* Headline */}
          <div
            className={cn(
              "space-y-6 transition-all duration-1000 delay-300",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              Claim Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-500 to-amber-600">
                BINGOLD
              </span>{" "}
              Rewards
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Easy, safe, and fast airdrop tasks on BNB Chain. Connect your
              wallet, complete simple actions, and get rewarded instantly.
            </p>
          </div>

          {/* CTA */}
          <div
            className={cn(
              "flex flex-col sm:flex-row gap-6 justify-center items-center transition-all duration-1000 delay-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
          >
            <Button
              onClick={() => {
                token
                  ? navigate(DASHBOARD_PATH.ALL_CAMPAIGNS)
                  : openConnectModal?.();
              }}
              size="lg"
              className={cn(
                "group relative px-10 py-7 text-xl font-bold bg-gradient-to-r from-yellow-400 to-amber-600 hover:from-yellow-300 hover:to-amber-500 text-black rounded-2xl",
                "shadow-2xl shadow-yellow-500/30 transition-all duration-500 hover:scale-105 hover:shadow-yellow-500/50"
              )}
            >
              <span className="flex items-center gap-3">
                {token || isConnected ? "Open Dashboard" : "Start Earning Now"}
                <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
              </span>
            </Button>

            <a
              href="#campaigns"
              className="text-lg text-gray-400 hover:text-yellow-400 font-medium flex items-center gap-2 transition-all hover:gap-4"
            >
              Explore Campaigns <ArrowRight className="w-5 h-5" />
            </a>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 max-w-5xl mx-auto">
            {Steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = i === activeStep;

              return (
                <div
                  key={i}
                  className={cn(
                    "flex flex-col items-center text-center transition-all duration-1000",
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-12",
                    `delay-${(i + 1) * 300}`
                  )}
                >
                  <div
                    className={cn(
                      "w-20 h-20 rounded-full flex items-center justify-center mb-8 transition-all duration-800",
                      isActive
                        ? "bg-gradient-to-br from-yellow-400 to-amber-600 scale-125 shadow-2xl shadow-yellow-500/60"
                        : "bg-white/5 border-4 border-white/10"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-10 h-10 transition-colors duration-800",
                        isActive ? "text-black" : "text-yellow-400"
                      )}
                    />
                  </div>

                  <h3
                    className={cn(
                      "text-2xl font-bold mb-4",
                      isActive ? "text-yellow-400" : "text-white"
                    )}
                  >
                    {step.title}
                  </h3>
                  <p
                    className={cn(
                      "text-base leading-relaxed px-4",
                      isActive ? "text-gray-200" : "text-gray-400"
                    )}
                  >
                    {step.subTitle}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Live Stats */}
          <div
            className={cn(
              "rounded-3xl bg-white/5 border border-white/10 p-10 transition-all duration-1000 delay-900",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            <div className="flex items-center justify-center gap-3 text-yellow-400 mb-8">
              <TrendingUp className="w-7 h-7 animate-pulse" />
              <span className="text-lg font-semibold">Live Platform Stats</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-center">
                <Coins className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">
                  {stats.totalDistributed > 0
                    ? `${stats.totalDistributed.toFixed(4)}`
                    : "—"}
                </div>
                <p className="text-gray-400">Total BINGOLD Distributed</p>
              </div>

              <div className="text-center">
                <Users className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">
                  {stats.totalParticipants > 0
                    ? stats.totalParticipants.toLocaleString()
                    : "—"}
                </div>
                <p className="text-gray-400">Active Participants</p>
              </div>

              <div className="text-center">
                <ShieldCheck className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <div className="text-4xl font-bold mb-2">
                  {stats.successRate > 0
                    ? `${stats.successRate.toFixed(1)}%`
                    : "—"}
                </div>
                <p className="text-gray-400">Success Rate</p>
              </div>
            </div>
          </div>
        </div>
      </Container>

      <style>{`
        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-40px) translateX(20px) rotate(90deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-20px) translateX(-30px) rotate(180deg);
            opacity: 0.4;
          }
          75% {
            transform: translateY(30px) translateX(10px) rotate(270deg);
            opacity: 0.6;
          }
        }
        .animate-float-slow {
          animation: float-slow linear infinite;
        }
      `}</style>
    </section>
  );
}
